import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { PeerType, UgcLiveStatus } from '@yandex-360/telemost-sdk/models';
import { ConnectionStatus, MediaStreamKind, SlotType } from '@yandex-video-platform/goloom-sdk';

import { RootState } from '../../../init-store';
import { ImageName } from '../../shared/components/svg-image/images';
import { AvatarPlaceholder } from '../../shared/helpers/avatar-placeholder';
import { PublicPermissionOverride, Role } from '../../shared/helpers/permissions';
import {
    AccessLevel,
    BroadcastData,
    ConferenceStateInfo,
    EmotionState,
    EmotionType,
    PeerId,
    ReactionState,
} from '../../types';

export type ParticipantID = string;

export interface TrackState {
    id: string;
    muted: boolean;
}

export interface Participant {
    id: ParticipantID;
    uid?: string;
    displayName?: string;
    shouldRenderAvatar: boolean;
    avatarUrl?: string;
    avatarPlaceholder: AvatarPlaceholder;
    isModerator: boolean;
    isOwner: boolean;
    isLocalRecording: boolean;

    // Данные свойства нужны обязательно, чтобы можно было задетектировать смену обертки MediaStream у треков (это
    // необходимо прежде всего для корректной работы в FF)
    audioStreamId?: string;
    videoStreamId?: string;
    desktopStreamId?: string;

    audioTrackState?: TrackState;
    videoTrackState?: TrackState;
    desktopTrackState?: TrackState;
    version?: number;
    reaction?: ReactionState;
    emotion?: EmotionState;

    peerType?: PeerType;
    refParticipantId?: ParticipantID | null;
}

export interface MediaTrack extends TrackState {
    track?: MediaStream;
    render: boolean;
}
export enum DisconnectReason {
    UNAUTHORIZED = 'unauthorized',
    UNKNOWN = 'unknown',
    KICK = 'kick',
    CLOSE_ROOM = 'close-room',
    END_CALL = 'end-call',
    END_CALL_BUTTON = 'end-call-button',
    PAGE_LEAVE = 'page-leave',
    ERROR_PAGE = 'error-page',
    DEBUG_HALT = 'debug-halt',
}
export interface ReconnectPayload {
    reason: string | null;
}

export interface ConferenceGuestConfig {
    audioMuted?: boolean;
    videoMuted?: boolean;
    displayName?: string;
}
export interface UserParams {
    id: string;
}
export interface CancelCallActionParams {
    /**
     * Не показывать фитбэк
     */
    skipFeedbackSaga?: boolean;
    source: DisconnectReason;
}

export interface DisconnectPayloadError {
    description?: string;
}
export interface DisconnectPayload {
    error?: DisconnectPayloadError;
    source: DisconnectReason;
}

export interface ToggleMuteActionInterface {
    value?: boolean;
    type: AudioVideoType;
    isTemporarily?: boolean;
    isOptimistic?: boolean;
}

export type AvailableResolutions = 720 | 1080;

export interface RecorderOptions {
    ws?: string;
    resolution?: AvailableResolutions;
}

export interface RecordConferenceActionInterface {
    recorder?: 'file' | 'ws';
    options: RecorderOptions;
}

export interface StopRecordConference {
    reason: 'by_user' | 'after_30_min' | 'kicked' | 'permission_deleted' | 'moderation_command' | 'tab_close';
    remove?: boolean;
}

export interface CreateBroadcastActionPayload {
    autoStart?: boolean;
    caption?: string;
    description?: string;
    accessLevel?: string;
}

export interface PeerState {
    id: ParticipantID;
    uid?: string;
    displayName?: string;
    avatarUrl?: string;
    avatarPlaceholder?: AvatarPlaceholder;
    isDefaultAvatar?: boolean;
    version?: number;
    peerType?: PeerType;
}

export type ParticipantState = PeerState & {
    isModerator?: boolean;
    isOwner?: boolean;
    audio?: TrackState;
    video?: TrackState;
    desktop?: TrackState;
    isLocalRecording?: boolean;
    probablyDead?: boolean;
    lastDominantTimestamp?: number;
    spokeLast?: number;
    reaction?: ReactionState;
    emotion?: EmotionState;
    publicPermissionsOverride?: Partial<Record<PublicPermissionOverride, boolean>>;
    isAssignedToSlot?: boolean;
    refParticipantId?: ParticipantID | null;
};

export type SlotState = {
    type: SlotType.SELF_VIEW | SlotType.PARTICIPANT;
    participant?: { userId: string } | null;
    mediaStreamKind?: MediaStreamKind | null;
};

export type WaitingRoomPeerState = PeerState & {
    waitingRoomEnteredAt: number;
    isAdmitting: boolean;
};

export type ParticipantsById = Partial<Record<ParticipantID, ParticipantState>>;

export interface ChatState {
    chatWidgetVisible: boolean;
    chatUnreadCount: number;
    chatId: string | null;
    isLoading: boolean;
    authUserChanged?: boolean;
    chatUserGuid: string | null;
    isChatWidgetReady: boolean;
    joinedChatId: Maybe<string>;
    chatHistory?: string;
    showChatRecordNotification?: boolean;
}

export enum DeviceAccessError {
    PermissionDenied = 'permission-denied',
    General = 'general',
}

export enum DeviceType {
    CAMERA = 'camera',
    MICROPHONE = 'microphone',
}

export interface VHLiveState {
    viewers: number;
    ugc_live_status?: UgcLiveStatus;
    start_time?: number;
}

export enum SidebarContent {
    Chat = 'chat',
    Participants = 'participants',
    Plugins = 'plugins',
}

export type ConferencePhone = { extension: string; number: string; uri: string };

export type ConferenceConfig = CloudAPI.ConferenceConnectionData & {
    resolved_uri: string;
    display_name?: string;
    phones?: ConferencePhone[];
    time?: string;
};

export enum WaitingRoomPeersSort {
    Time = 'time',
    Alfa = 'alfa',
}

export interface JoinConferencePayload {
    roomId: string;
    searchString?: string;
    isReconnect?: boolean;
}

export interface LastNChangeSpec {
    remove: ParticipantID[];
    add: ParticipantID[];
    showMyVideo: boolean;
}

export interface ChangeDominantSpeakerPayload {
    userId: ParticipantID;
    showMyVideo: boolean;
}

export enum NotificationId {
    SHARE = 'share',
    SCREEN_SHARING_IS_ACTIVE = 'screen-sharing-is-active',
    SCREEN_SHARING_ACCESS_ERROR = 'screen-sharing-access-error',
    CAMERA_ACCESS_ERROR = 'camera-access-error',
    MICROPHONE_ACCESS_ERROR = 'microphone-access-error',
    ONLINE = 'online',
    OFFLINE = 'offline',
    RECONNECTING = 'reconnecting',
    LIMIT_ERROR = 'limit-error',
    FEEDBACK = 'feedback',
    SUPPORT_SERVICE_APPLY = 'support-service-apply',

    RECORDING_ERROR = 'recording-error',
    RECORDING_WAITING_DOWNLOAD = 'recording-waiting-download',
    CHAT_CREATE_ERROR = 'chat-create-error',
    CHAT_OPEN_ERROR = 'chat-open-error',
    PLEASE_UNMUTE = 'please-unmute',
    CHAT_HISTORY_SAVE = 'chat-history-save',
    CONNECT_TO_MEETING_BY_ID_INFO = 'connect-to-meeting-by-id-info',
    PLAN_CALL_SUCCESS = 'plan-call-success',
    PLAN_CALL_ERROR = 'plan-call-error',
    MODERATION_MUTE_AUDIO = 'moderation-mute-audio',
    MODERATION_MUTE_VIDEO = 'moderation-mute-video',
    MODERATION_MUTE_SCREEN_SHARING = 'moderation-mute-screen-sharing',
    MODERATION_MUTE_RECORDING_SUCCESS = 'moderation-mute-recording-success',
    MODERATION_MUTE_RECORDING_ERROR = 'moderation-mute-recording-error',
    MODERATION_MUTE_ALL_RECORDING_SUCCESS = 'moderation-mute-all-recording-success',
    MODERATION_MUTE_ALL_RECORDINGS_ERROR = 'moderation-mute-all-recordings-error',
    MODERATION_NO_PERMISSION_TO_MUTE_RECORDINGS = 'moderation-no-permission-to-mute-recordings',
    MODERATION_IAM_NEW_MODERATOR = 'moderation-iam-new-moderator',
    MODERATION_SOMEBODY_NEW_MODERATOR = 'moderation-somebody-new-moderator',
    MODERATION_IAM_NOT_MODERATOR = 'moderation-iam-not-moderator',
    MODERATION_NO_LONGER_MODERATOR = 'moderation-no-longer-moderator',
    MODERATION_REMOVE_PARTICIPANT = 'moderation-remove-participant',
    MODERATION_ERROR = 'moderation-error',
    MODERATION_MAKE_MEMBER_ERROR = 'moderation-make-member-error',
    MODERATION_MAKE_ADMIN_ERROR = 'moderation-make-admin-error',
    MODERATION_KICK = 'moderation-kick',
    MODERATION_KICK_BAN = 'moderation-kick-ban',

    CONFERENCE_ACCESS_LEVEL_CHANGE = 'conference-access-level-change',
    BROADCAST_ACCESS_LEVEL_CHANGE = 'broadcast-access-level-change',
    CREATE_BROADCAST_FAILED = 'create_broadcast_failed',
    START_BROADCAST_FAILED = 'start_broadcast_failed',
    STOP_BROADCAST_FAILED = 'stop_broadcast_failed',
    END_BROADCAST_FAILED = 'end_broadcast_failed',

    NOISE_CANCELLATION_STATUS = 'noise-cancellation-status',
    JOINING = 'joining',
    SEND_FEEDBACK_ERROR = 'send-feedback-error-notification',

    PERMISSIONS_CLOSE_ROOM = 'permissions-close-room',
    NO_PERMISSION_TO_CLOSE_ROOM = 'no-permission-to-close-room',
    PERMISSIONS_ERROR = 'permissions-error',

    WAITING_ROOM_NEW_PEER = 'waiting-room-new-peer',
    WAITING_ROOM_NEW_PEERS = 'waiting-room-new-peers',
    WAITING_ROOM_ADMIT_ERROR = 'waiting-room-admit-error',
    WAITING_ROOM_ADMIT_ALL_ERROR = 'waiting-room-admit-all-error',
    WAITING_ROOM_CONFERENCE_LIMIT = 'waiting-room-conference-limit',
    WAITING_ROOM_OVERFLOW = 'waiting-room-overflow',

    NETWORK_QUALITY_BAD = 'network-quality-bad',
    NETWORK_QUALITY_HIDE_VIDEO = 'network-quality-hide-video',
    NETWORK_QUALITY_SHOW_VIDEO = 'network-quality-show-video',

    CLOUD_RECORDING_START = 'cloud-recording-start',
    CLOUD_RECORDING_START_FAILED = 'cloud-recording-start-failed',
    CLOUD_RECORDING_STOP = 'cloud-recording-stop',
    CLOUD_RECORDING_STOP_FAILED = 'cloud-recording-stop-failed',

    SUMMARIZATION_START = 'summarization-start',
    SUMMARIZATION_START_FAILED = 'summarization-start-failed',
    SUMMARIZATION_STOP = 'summarization-stop',
    SUMMARIZATION_STOP_FAILED = 'summarization-stop-failed',
    SUMMARIZATION_MAIL_RECIPIENT = 'summarization-mail-recipient',
    SUMMARIZATION_MAIL_RECIPIENT_UPDATE_ERROR = 'summarization-mail-recipient-update-error',
    SUMMARIZATION_NO_PERMISSION_TO_START = 'summarization-no-permission-to-start',
    SUMMARIZATION_NO_PERMISSION_TO_STOP = 'summarization-no-permission-to-stop',

    SET_REACTION_ERROR = 'set-reaction-error',

    REPORT_A_PROBLEM_SUCCESS = 'report-a-problem-success',
    REPORT_A_PROBLEM_FAILED = 'report-a-problem-failed',

    PERMISSIONS_EXTENDED_APPLY_SUCCESS = 'permissions-extended-apply-success',
    PERMISSIONS_EXTENDED_APPLY_ERROR = 'permissions-extended-apply-error',
    PERMISSIONS_EXTENDED_MATRIX_EDIT_REVOKED = 'permissions-extended-matrix-edit-revoked',
    NO_PERMISSION_TO_USE_MICROPHONE = 'no-permission-to-use-microphone',
    NO_PERMISSION_TO_USE_CAMERA = 'no-permission-to-use-camera',
    NO_PERMISSION_TO_SHARE_DESKTOP = 'no-permission-to-share-desktop',
    INVITATION_COPIED = 'invitation-copied',
    LINK_COPIED = 'link-copied',
    TELE_MESSENGER_LINK_COPIED = 'tele-messenger-link-copied',
    LINK_COPIED_CONF_HISTORY = 'link-copied-conf-history',
    PHONE_COPIED = 'phone-copied',
    CONF_HISTORY_FETCHING_FAILED = 'conf-history-fetching-failed',
    EMAIL_COPIED = 'email-copied',
    REPORT_ERROR = 'report-error',
    CONNECTION_LOST = 'connection-lost',
}

export enum NotificationGroup {
    NETWORK_STATUS = 'network-status',
    NETWORK_QUALITY = 'network-quality',
    PARTICIPANTS_VIDEO = 'participants-video',
}

export type AudioVideoType = 'audio' | 'video';
export type MediaType = AudioVideoType | 'desktop';

export type Constraints = {
    video: MediaTrackConstraints;
};

export enum ConnectionErrors {
    USER_KICKED_ERROR = 'TelemostUserKickedError',
    USER_KICKED_WITH_BAN_ERROR = 'TelemostUserKickedWithBanError',
    ORGANIZATION_MISMATCHES_ERROR = 'TelemostOrganizationMismatchesError',
}

export interface StreamToVideosMap {
    byTrack: Map<MediaStreamTrack, HTMLVideoElement>;
    byTrackId: Map<string, HTMLVideoElement>;
}

export interface ParticipantDrawerParams {
    isDesktopTrack: Maybe<boolean>;
    video: Maybe<HTMLVideoElement>;
    hasFrameBorder: Maybe<boolean>;
    statusIcons?: ImageName[];
}

export interface RecordSagaHelpers {
    getAudioStreamTracks(state: RootState): MediaStreamTrack[];
    getParticipantDrawerParams(
        participant: Participant,
        streamToVideosMap: StreamToVideosMap,
        isDrawDesktopTrack: boolean,
        isDrawPresenter: boolean
    ): ParticipantDrawerParams;
    canStartRecording(): boolean;
}

export enum OutgoingCallStatuses {
    RINGING = 'ringing',
    CALLEE_CONNECTING = 'callee_connecting',
}

export interface AccessLevelData {
    conference?: {
        access_level: AccessLevel;
    };
    broadcast?: {
        access_level: AccessLevel;
    };
}

export interface AdmitParams {
    toLimit?: boolean;
    fromToast?: boolean;
}

export interface AdmitOneParams extends AdmitParams {
    peerId: string;
    uid?: string;
}

export interface PollWaitingRoomPayload {
    roomId: string;
    config: ConferenceConfig;
}

export type IndicatorDotColor = 'green' | 'red';

export type NotificationType = 'chat_created' | 'peers_state' | 'waiting_room_update' | 'emotion';

export type EmotionMessage = {
    peer_id: PeerId;
    emotion_type: EmotionType;
    show_deadline: number;
    duration: number;
};

export type NotificationPayload = Record<string, unknown>;

export interface NotificationEvent {
    notificationId: string;
    payload: string;
}

export type NotificationObject =
    | {
          type: Exclude<NotificationType, 'emotion'>;
          payload: NotificationPayload;
      }
    | {
          type: 'emotion';
          payload: EmotionMessage;
      };

export interface ChatCreatedPayload {
    chat_path: string;
}

export interface PeersStatePayload {
    peers: CloudAPI.PeerInfo[];
}

export interface ConferenceStatePayload {
    conference: ConferenceStateInfo;
}

export interface PermissionsStatePayload {
    permissions: CloudAPI.PermissionsInfo;
}

export interface PassportAccountModificationPayload {
    peer_id: string;
}

export enum MessageEventType {
    RoleChanged = 'role_changed_event',
}

export interface RoleChangedPayload {
    event: {
        peer_id: string;
        type: MessageEventType.RoleChanged;
        user_role: Role;
    };
    source: {
        type: string;
        uid: string;
    };
}

export interface BroadcastDataPayload {
    broadcast_data: BroadcastData;
}

export interface ConnectionStatusEvent {
    current: ConnectionStatus;
    prev: Maybe<ConnectionStatus>;
}

export interface PermissionsModificationPayload {
    type: 'permissions_modification';
}

export interface SummarizationSettingsUpdatePayload {
    type: 'summarization_settings_update';
}

export type ShareCallPayload = Maybe<{
    calledOnJoin: boolean;
}>;

export interface EndBroadcastActionPayload {
    /** @default {false} */
    notifyOnError?: boolean;
}

export interface PermissionsClientConfiguration {
    extended_role_permissions_enabled?: boolean;
    calendar_summarization_receive_available?: boolean;
    summarization_available?: boolean;
    cloud_recording_available?: boolean;
}
